/**
 * Google AdSense Configuration Service
 * Manages AdSense settings and ad unit configurations
 */

export interface AdSenseConfig {
  clientId: string;
  enabled: boolean;
  adSlots: {
    square: string;
    infeed: string;
    banner: string;
    rectangle: string;
    leaderboard: string;
    interstitial: string;
  };
}

export interface AdUnitConfig {
  slot: string;
  size: 'banner' | 'rectangle' | 'leaderboard' | 'responsive';
  format?: string;
  responsive?: boolean;
}

class AdSenseConfigService {
  private config: AdSenseConfig;

  constructor() {
    this.config = {
      clientId: process.env.VUE_APP_ADSENSE_CLIENT_ID || '',
      enabled: process.env.VUE_APP_ADSENSE_ENABLED === 'true',
      adSlots: {
        square: process.env.VUE_APP_ADSENSE_SQUARE_SLOT || '7143211930',
        infeed: process.env.VUE_APP_ADSENSE_INFEED_SLOT || '8323629337',
        banner: process.env.VUE_APP_ADSENSE_BANNER_SLOT || '',
        rectangle: process.env.VUE_APP_ADSENSE_RECTANGLE_SLOT || '',
        leaderboard: process.env.VUE_APP_ADSENSE_LEADERBOARD_SLOT || '',
        interstitial: process.env.VUE_APP_ADSENSE_INTERSTITIAL_SLOT || ''
      }
    };
  }

  /**
   * Get the AdSense client ID
   */
  getClientId(): string {
    return this.config.clientId;
  }

  /**
   * Check if AdSense is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled && !!this.config.clientId;
  }

  /**
   * Get ad slot ID by type
   */
  getAdSlot(type: 'square' | 'infeed' | 'banner' | 'rectangle' | 'leaderboard' | 'interstitial'): string {
    return this.config.adSlots[type];
  }

  /**
   * Get ad unit configuration for different ad types
   */
  getAdUnitConfig(type: 'square' | 'infeed' | 'banner' | 'rectangle' | 'leaderboard' | 'interstitial'): AdUnitConfig {
    const slot = this.getAdSlot(type);
    
    const configs: Record<string, AdUnitConfig> = {
      square: {
        slot,
        size: 'banner',
        format: 'auto',
        responsive: true
      },
      infeed: {
        slot,
        size: 'responsive',
        format: 'autorelaxed',
        responsive: true
      },
      banner: {
        slot,
        size: 'banner',
        format: 'auto',
        responsive: true
      },
      rectangle: {
        slot,
        size: 'rectangle',
        format: 'rectangle',
        responsive: true
      },
      leaderboard: {
        slot,
        size: 'leaderboard',
        format: 'auto',
        responsive: true
      },
      interstitial: {
        slot,
        size: 'responsive',
        format: 'auto',
        responsive: true
      }
    };

    return configs[type] || configs.banner;
  }

  /**
   * Get all configuration
   */
  getConfig(): AdSenseConfig {
    return { ...this.config };
  }

  /**
   * Validate configuration
   */
  isConfigValid(): boolean {
    if (!this.config.enabled) return true;
    
    return !!(
      this.config.clientId &&
      this.config.clientId.startsWith('ca-pub-') &&
      Object.values(this.config.adSlots).some(slot => slot.length > 0)
    );
  }

  /**
   * Get missing configuration items
   */
  getMissingConfig(): string[] {
    const missing: string[] = [];
    
    if (!this.config.clientId) {
      missing.push('VUE_APP_ADSENSE_CLIENT_ID');
    }

    if (!this.config.adSlots.square) {
      missing.push('VUE_APP_ADSENSE_SQUARE_SLOT');
    }

    if (!this.config.adSlots.infeed) {
      missing.push('VUE_APP_ADSENSE_INFEED_SLOT');
    }
    
    if (!this.config.adSlots.banner) {
      missing.push('VUE_APP_ADSENSE_BANNER_SLOT');
    }
    
    if (!this.config.adSlots.rectangle) {
      missing.push('VUE_APP_ADSENSE_RECTANGLE_SLOT');
    }
    
    if (!this.config.adSlots.leaderboard) {
      missing.push('VUE_APP_ADSENSE_LEADERBOARD_SLOT');
    }
    
    if (!this.config.adSlots.interstitial) {
      missing.push('VUE_APP_ADSENSE_INTERSTITIAL_SLOT');
    }
    
    return missing;
  }
}

// Export singleton instance
export const adSenseConfig = new AdSenseConfigService();
export default adSenseConfig;